import { useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import {
    INodeAction,
    NodeActionScope,
} from '@/features/drive/functionalities/actions/node/core/shared/utils/node-action-helpers'
import { useNodeItemContext } from '@/features/drive/entities/node/node-item-context'
import { compact } from '@/utils/functions/compact'
import { useUser } from '@clerk/nextjs'
import { useCallback } from 'react'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'
import { NodeType } from '@/api/nodes/mutations/directory/create-directory-mutation'

export const useNodeActionHelpers = () => {
    const { node } = useNodeItemContext()
    const { isPreview } = useDirectoryContext()

    const {
        state: { selectedCount },
    } = useDirectorySelectionContext()

    const { user } = useUser()
    const isSuperUser = user?.publicMetadata.isSuperUser == true

    /**
     * Defines, based on the current state, which scopes are active
     */
    const getCurrentActiveScopes = useCallback((): NodeActionScope[] => {
        return compact([selectedCount > 1 && NodeActionScope.SELECTED_MULTIPLE, isPreview && NodeActionScope.PREVIEW])
    }, [selectedCount, isPreview])

    const getShouldShowAction = useCallback(
        (action: Pick<INodeAction, 'availableForScopes' | 'superUserScopeOverride' | 'applicableForTypes'>) => {
            const activeScopes = getCurrentActiveScopes()
            const availableScopes = action.availableForScopes
            const superUserOverrides = action.superUserScopeOverride ?? []

            // Check if action is applicable for this node type
            if (action.applicableForTypes && !action.applicableForTypes.includes(node.type as NodeType)) {
                return false
            }

            // If no scope restrictions, action is available in all scopes
            if (!availableScopes) return true

            // Check if all active scopes are in the available scopes
            const hasAvailableScope = activeScopes.every((scope) => availableScopes.includes(scope))

            // Super users can access via override scopes even if not normally available
            const hasSuperUserOverride = isSuperUser && activeScopes.some((scope) => superUserOverrides.includes(scope))

            return hasAvailableScope || hasSuperUserOverride
        },
        [getCurrentActiveScopes, isSuperUser, node.type]
    )

    return { getShouldShowAction, getCurrentActiveScopes }
}
